import { supabase } from '@/lib/supabase'

export type Budget = {
  id: string
  category_id: string
  category_name: string | null
  amount: number
  sort_order: number
}

export type BudgetStatusItem = {
  category_id: string
  category_name: string
  budget_amount: number
  spent_amount: number
  remaining: number
  sort_order: number
  color: string | null
  parent_category_id: string | null
  is_subcategory_budget: boolean
}

export type BudgetStatus = {
  month: string
  items: BudgetStatusItem[]
}

export function enrichBudgets(
  budgets: Array<{ id: string; category_id: string; amount: number }>,
  categories: Array<{ id: string; name: string; sort_order: number | null }>,
): Budget[] {
  const categoryMap = new Map(categories.map(c => [c.id, c]))
  return budgets
    .map(b => {
      const cat = categoryMap.get(b.category_id)
      return {
        id: b.id,
        category_id: b.category_id,
        category_name: cat?.name ?? null,
        amount: b.amount,
        sort_order: cat?.sort_order ?? 0,
      }
    })
    .sort((a, b) => a.sort_order - b.sort_order)
}

export function transformBudgetStatus(
  rows: Array<{ category_id: string; category_name: string; budget_amount: number; spent_amount: number; sort_order: number; color: string | null; parent_category_id: string | null; is_subcategory_budget: boolean }>,
): BudgetStatusItem[] {
  return rows.map((row) => ({
    category_id: row.category_id,
    category_name: row.category_name,
    budget_amount: row.budget_amount,
    spent_amount: row.spent_amount,
    remaining: row.budget_amount - row.spent_amount,
    sort_order: row.sort_order,
    color: row.color,
    parent_category_id: row.parent_category_id,
    is_subcategory_budget: row.is_subcategory_budget,
  }))
}

export async function fetchBudgets(userId?: string | null): Promise<Budget[]> {
  let query = supabase
    .from('budgets')
    .select('id, category_id, amount, user_id')

  if (userId) {
    query = query.eq('user_id', userId)
  } else {
    query = query.is('user_id', null)
  }

  const { data, error: budgetError } = await query
  if (budgetError) throw new Error(budgetError.message)

  const { data: categories, error: catError } = await supabase
    .from('categories')
    .select('id, name, sort_order')
  if (catError) throw new Error(catError.message)

  return enrichBudgets(data ?? [], categories ?? [])
}

export async function saveBudgets(
  budgets: Array<{ category_id: string; amount: number | null }>,
  userId?: string | null,
): Promise<Budget[]> {
  const categoryIds = budgets.map(b => b.category_id)
  const { data: categories, error: catError } = await supabase
    .from('categories')
    .select('id, parent_id, owner_id')
    .in('id', categoryIds)

  if (catError) throw new Error(catError.message)

  const categoryLookup = new Map(categories?.map(c => [c.id, c]) ?? [])

  for (const budget of budgets) {
    const cat = categoryLookup.get(budget.category_id)
    if (!cat) throw new Error(`Category ${budget.category_id} not found`)
    // Personal categories cannot have household budgets
    if (!userId && cat.owner_id && budget.amount !== null) {
      throw new Error('個人カテゴリーに家計の予算は設定できません。')
    }
  }

  // Mutual exclusion: parent budget vs subcategory budgets
  const parentBudgetsToSet = budgets.filter(b => {
    const cat = categoryLookup.get(b.category_id)
    return cat && cat.parent_id === null && b.amount !== null
  })
  const subcategoryBudgetsToSet = budgets.filter(b => {
    const cat = categoryLookup.get(b.category_id)
    return cat && cat.parent_id !== null && b.amount !== null
  })

  if (parentBudgetsToSet.length > 0) {
    const parentIds = parentBudgetsToSet.map(b => b.category_id)
    const { data: children } = await supabase
      .from('categories')
      .select('id')
      .in('parent_id', parentIds)
    if (children && children.length > 0) {
      let subQuery = supabase
        .from('budgets')
        .select('id')
        .in('category_id', children.map(c => c.id))
      if (userId) {
        subQuery = subQuery.eq('user_id', userId)
      } else {
        subQuery = subQuery.is('user_id', null)
      }
      const { data: existingSub } = await subQuery
      if (existingSub && existingSub.length > 0) {
        throw new Error('サブカテゴリに予算が設定されています。先にサブカテゴリの予算をクリアしてください。')
      }
    }
  }

  if (subcategoryBudgetsToSet.length > 0) {
    const parentIds = [...new Set(subcategoryBudgetsToSet.map(b => {
      const cat = categoryLookup.get(b.category_id)!
      return cat.parent_id!
    }))]
    let parentQuery = supabase
      .from('budgets')
      .select('id')
      .in('category_id', parentIds)
    if (userId) {
      parentQuery = parentQuery.eq('user_id', userId)
    } else {
      parentQuery = parentQuery.is('user_id', null)
    }
    const { data: existingParent } = await parentQuery
    if (existingParent && existingParent.length > 0) {
      throw new Error('親カテゴリに予算が設定されています。先に親カテゴリの予算をクリアしてください。')
    }
  }

  // Fetch all existing budgets for the given categories in one query
  let existingQuery = supabase
    .from('budgets')
    .select('id, category_id')
    .in('category_id', categoryIds)

  if (userId) {
    existingQuery = existingQuery.eq('user_id', userId)
  } else {
    existingQuery = existingQuery.is('user_id', null)
  }

  const { data: existingBudgets, error: existError } = await existingQuery
  if (existError) throw new Error(existError.message)

  const existingMap = new Map(
    (existingBudgets ?? []).map(b => [b.category_id, b.id])
  )

  // Classify operations
  const toInsert: Array<{ category_id: string; amount: number; user_id: string | null }> = []
  const toUpdate: Array<{ id: string; amount: number }> = []
  const toDeleteCategoryIds: string[] = []

  for (const budget of budgets) {
    const existingId = existingMap.get(budget.category_id)
    if (typeof budget.amount === 'number') {
      if (existingId) {
        toUpdate.push({ id: existingId, amount: budget.amount })
      } else {
        toInsert.push({
          category_id: budget.category_id,
          amount: budget.amount,
          user_id: userId || null,
        })
      }
    } else if (budget.amount === null && existingId) {
      toDeleteCategoryIds.push(budget.category_id)
    }
  }

  // Execute batch operations in parallel
  const ops: PromiseLike<void>[] = []

  if (toInsert.length > 0) {
    ops.push(
      supabase.from('budgets').insert(toInsert).then(({ error }) => {
        if (error) throw new Error(error.message)
      })
    )
  }

  // Updates must be individual (different amounts per row), but run in parallel
  for (const { id, amount } of toUpdate) {
    ops.push(
      supabase.from('budgets').update({ amount }).eq('id', id).then(({ error }) => {
        if (error) throw new Error(error.message)
      })
    )
  }

  if (toDeleteCategoryIds.length > 0) {
    let deleteQuery = supabase
      .from('budgets')
      .delete()
      .in('category_id', toDeleteCategoryIds)

    if (userId) {
      deleteQuery = deleteQuery.eq('user_id', userId)
    } else {
      deleteQuery = deleteQuery.is('user_id', null)
    }

    ops.push(
      deleteQuery.then(({ error }) => {
        if (error) throw new Error(error.message)
      })
    )
  }

  await Promise.all(ops)

  return fetchBudgets(userId)
}

export async function fetchBudgetStatus(
  month?: string,
  scope: 'household' | 'personal' | 'all' = 'household',
  userId?: string | null,
  expenseType?: string | null,
): Promise<BudgetStatus> {
  const m = month || (() => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  })()

  const { data, error } = await supabase.rpc('get_budget_status', {
    p_year_month: m,
    p_scope: scope,
    p_user_id: userId || null,
    p_expense_type: expenseType && expenseType !== 'all' ? expenseType : null,
  })

  if (error) throw new Error(error.message)

  // Cast needed because Supabase-generated types may not include new RPC columns yet
  const rows = (data ?? []) as Array<{
    category_id: string
    category_name: string
    budget_amount: number
    spent_amount: number
    sort_order: number
    color: string | null
    parent_category_id: string | null
    is_subcategory_budget: boolean
  }>
  const items = transformBudgetStatus(rows)

  return { month: m, items }
}
